/* eslint-disable @typescript-eslint/naming-convention */
import { ID } from '@datorama/akita';
import { Point, Polygon } from 'ol/geom';

import { ColumnType, LanguagePage, PageTitles } from '../interfaces/types';
import { Entity, Column } from '../entity/index';
import { IEntity, IGeoServer } from '../interfaces/models';
import { Territory } from './territory.model';

export interface ITerritoryFsa extends IEntity {
  coid: string;
  fsa: string;
  territoryId: string;
}

export interface IFsa extends IEntity {
  name: string;
  addressNumber: number;
  sfu: number;
  sxu: number;
  mdu: number;
  mxu: number;
  others: number;
  geometry: Polygon;
  point: Point;
  territory: string;
  checked: boolean;
}

@Entity({
  name: PageTitles.fsa,
  language: LanguagePage.fsa,
})
export class Fsa implements IFsa {
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  name: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
    },
  })
  addressNumber: number;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 3,
    },
  })
  sfu: number;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 4,
    },
  })
  sxu: number;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 5,
    },
  })
  mdu: number;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 6,
    },
  })
  mxu: number;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 7,
    },
  })
  others: number;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 8,
    },
  })
  territory: string;

  id: ID;
  checked: boolean;
  geometry: Polygon;
  point: Point;

  constructor(
    fsa: IFsa = {
      id: '',
      name: '',
      addressNumber: 0,
      sfu: 0,
      sxu: 0,
      mdu: 0,
      mxu: 0,
      others: 0,
      geometry: new Polygon([]),
      point: new Point([]),
      territory: '',
      checked: false,
    }
  ) {
    const {
      id,
      name,
      addressNumber,
      sfu,
      sxu,
      mdu,
      mxu,
      others,
      geometry,
      point,
      territory,
      checked,
    } = fsa;

    this.id = id;
    this.name = name;
    this.addressNumber = addressNumber;
    this.sfu = sfu;
    this.sxu = sxu;
    this.mdu = mdu;
    this.mxu = mxu;
    this.others = others;
    this.geometry = geometry;
    this.point = point;
    this.territory = territory;
    this.checked = checked;
  }

  static createFromDTO(
    myWorldData: IGeoServer,
    territories: Territory[],
    fsas: ITerritoryFsa[]
  ): Fsa {
    const {
      attr11,
      cfsauid,
      premise_sfu_cnt,
      premise_sxu_cnt,
      premise_mdu_cnt,
      premise_mxu_cnt,
      premise_other_cnt,
    } = myWorldData.properties;

    let indexTerritory = -1;
    if (fsas && fsas.length > 0) {
      const fas = fsas.filter((f) => f.fsa === attr11 || f.fsa === cfsauid);
      if (fas.length > 0) {
        indexTerritory = territories.findIndex(
          (t) => t.id === fas[0].territoryId
        );
      }
    }

    return new Fsa({
      id: attr11 ?? cfsauid,
      name: attr11 ?? cfsauid,
      addressNumber:
        premise_sfu_cnt +
        premise_sxu_cnt +
        premise_mdu_cnt +
        premise_mxu_cnt +
        premise_other_cnt,
      sfu: premise_sfu_cnt,
      sxu: premise_sxu_cnt,
      mdu: premise_mdu_cnt,
      mxu: premise_mxu_cnt,
      others: premise_other_cnt,
      geometry: new Polygon(
        myWorldData.geometry.coordinates.map((i) => i.map((c) => [c[1], c[0]]))
      ),
      point: new Point(
        myWorldData.geometry.coordinates.map((i) =>
          i.map((j) => j.map((k) => [k[1], k[0]]))
        )
      ),
      territory:
        indexTerritory > -1 ? territories[indexTerritory].territoryName : '',
      checked: false,
    });
  }
}
